// audiobooks/librivoxProgressService.ts
// Progresso e favoritos dos audiobooks do LibriVox, salvos no D1 (tabela "libri-audiobooks")
// via /api/audiobooks/librivox. O token de login é anexado pelo services/apiAuthFetch.
import type { LibriVoxBook } from './librivoxApi';

export interface LibrivoxProgress {
  librivox_id: string;
  title?: string | null;
  author?: string | null;
  language?: string | null;
  cover_url?: string | null;
  total_sections: number;
  is_favorited: boolean;
  current_section: number | null;
  current_section_title?: string | null;
  position_seconds: number;
  section_duration: number;
  completed_sections: number[];
  favorite_sections: number[];
  progress_percentage: number;
  last_listened_at?: string | null;
}

const LOCAL_KEY = (id: string) => `libritui_librivox_progress_${id}`;

const readLocal = (id: string): LibrivoxProgress | null => {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY(id)) || 'null'); } catch { return null; }
};
const writeLocal = (p: LibrivoxProgress | null) => {
  if (!p) return;
  try { localStorage.setItem(LOCAL_KEY(p.librivox_id), JSON.stringify(p)); } catch { /* cota cheia */ }
};

const bookMeta = (book: LibriVoxBook) => ({
  librivox_id: String(book.id),
  title: book.title,
  author: [book.authors?.[0]?.first_name, book.authors?.[0]?.last_name].filter(Boolean).join(' ') || null,
  language: book.language || null,
  cover_url: book.coverart_thumbnail || null,
  total_sections: book.sections?.length || Number((book as any).num_sections) || 0,
});

const call = async (payload: Record<string, unknown>): Promise<LibrivoxProgress | null> => {
  const res = await fetch('/api/audiobooks/librivox', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = await res.json();
  return (j.item as LibrivoxProgress) || null;
};

export const librivoxProgressService = {
  /** Lê do cache local na hora (o D1 atualiza em seguida). */
  cached: (id: string) => readLocal(id),

  async get(id: string): Promise<LibrivoxProgress | null> {
    try {
      const item = await call({ action: 'get', librivox_id: id });
      if (item) writeLocal(item);
      return item || readLocal(id);
    } catch {
      return readLocal(id);
    }
  },

  async list(favoritesOnly = false): Promise<LibrivoxProgress[]> {
    try {
      const res = await fetch('/api/audiobooks/librivox', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', favorites_only: favoritesOnly }),
      });
      if (!res.ok) return [];
      return (await res.json()).items || [];
    } catch {
      return [];
    }
  },

  async toggleFavorite(book: LibriVoxBook, isFavorited: boolean) {
    const local = readLocal(String(book.id));
    if (local) writeLocal({ ...local, is_favorited: isFavorited });
    const item = await call({ action: 'toggle_favorite', ...bookMeta(book), is_favorited: isFavorited });
    writeLocal(item);
    return item;
  },

  async toggleSectionFavorite(book: LibriVoxBook, sectionNumber: number, isFavorited: boolean) {
    const item = await call({ action: 'toggle_section_favorite', ...bookMeta(book), section_number: sectionNumber, is_favorited: isFavorited });
    writeLocal(item);
    return item;
  },

  async saveProgress(book: LibriVoxBook, section: { section_number: string | number; title: string }, positionSeconds: number, sectionDuration: number, completed = false) {
    const meta = bookMeta(book);
    // grava local primeiro: se a rede cair, a posição não se perde
    const prev = readLocal(meta.librivox_id);
    const sectionNo = Number(section.section_number);
    const done = new Set(prev?.completed_sections || []);
    if (completed || (sectionDuration > 0 && positionSeconds / sectionDuration >= 0.95)) done.add(sectionNo);
    writeLocal({
      ...(prev || { is_favorited: false, favorite_sections: [], progress_percentage: 0 }),
      ...meta,
      current_section: sectionNo,
      current_section_title: section.title,
      position_seconds: positionSeconds,
      section_duration: sectionDuration,
      completed_sections: [...done].sort((a, b) => a - b),
    } as LibrivoxProgress);
    try {
      const item = await call({
        action: 'save_progress', ...meta,
        current_section: sectionNo, current_section_title: section.title,
        position_seconds: Math.round(positionSeconds * 10) / 10, section_duration: Math.round(sectionDuration * 10) / 10,
        section_completed: completed,
      });
      writeLocal(item);
      return item;
    } catch {
      return readLocal(meta.librivox_id);
    }
  },
};
